import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { configureStore } from '@reduxjs/toolkit';
import { AuthProvider, useAuth } from 'react-oidc-context';
import { Layout } from './components/Layout';
import { Users } from './pages/Users';
import { Organizations } from './pages/Organizations';
import { SubscriptionPeriods } from './pages/SubscriptionPeriods';
import { TariffUsage } from './pages/TariffUsage';
import { Activity } from './pages/Activity';
import { AuthCallback } from './pages/AuthCallback';
import { IdeaDetails } from './pages/IdeaDetails';
import { Ideas } from './pages/Ideas';
import { api } from './api/api';
import { User } from 'oidc-client-ts';
import { ReleaseNotes } from './pages/ReleaseNotes.tsx';

const store = configureStore({
  reducer: {
    [api.reducerPath]: api.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware()
      .concat(api.middleware),
});

const oidcConfig = {
  authority: import.meta.env.VITE_APP_KEYCLOAK_AUTH_ENDPOINT,
  client_id: import.meta.env.VITE_APP_KEYCLOAK_CLIENT_ADMIN,
  redirect_uri: `${window.location.origin}/auth/success`,
  response_type: "code",
  scope: "openid profile",
  pkce: true,
  post_logout_redirect_uri: window.location.origin,
  onSigninCallback: (_user: User | undefined): void => {
    // Don't clear the URL here
    return;
  }
};

const AppContent = () => {
  const auth = useAuth();

  useEffect(() => {
    if (auth.user?.access_token) {
      sessionStorage.setItem('auth_token', auth.user.access_token);
    }
  }, [auth.user?.access_token]);

  useEffect(() => {
    console.log('')
    if (!auth.isLoading && !auth.isAuthenticated && window.location.pathname !== '/auth/success') {
      console.log('should redirect');
      sessionStorage.setItem('redirectPath', window.location.pathname + window.location.search);
      auth.signinRedirect();
    }
  }, [auth.isLoading, auth.isAuthenticated, auth]);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/auth/success" element={<AuthCallback />} />
        <Route path="/" element={<Navigate to="/organizations" replace />} />
        <Route element={<Layout />}>
          <Route path="/users" element={<Users />} />
          <Route path="/organizations" element={<Organizations />} />
          <Route path="/subscription-periods" element={<SubscriptionPeriods />} />
          <Route path="/tariff-usage" element={<TariffUsage />} />
          <Route path="/ideas" element={<Ideas />} />
          <Route path="/ideas/:ideaId" element={<IdeaDetails />} />
          <Route path="/activity" element={<Navigate to="/activity/paid" replace />} />
          <Route path="/activity/:purposeType" element={<Activity />} />
          <Route path="/release_notes" element={<ReleaseNotes />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export const App = () => {
  return (
    <Provider store={store}>
      <AuthProvider {...oidcConfig}>
        <AppContent />
      </AuthProvider>
    </Provider>
  );
};
